import { TmElement } from '@root/packages/lit-sdk/src';
import { html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { translate } from '@thiememeulenhoff/translations';
import { tagName } from '../../helpers';
import { styles } from './text-collapse.styles';

const tag = tagName('text-collapse');

@customElement(tag)
export class TextCollapseComponent extends TmElement {
  static override styles = styles;

  @property({ type: Number })
  lines = 3;

  @property({ type: Boolean, attribute: 'observe-resize' })
  observeResize = false;

  @query('a')
  button!: HTMLElement;

  @query('.truncate')
  truncate!: HTMLElement;

  @query('.truncate__inner')
  truncateInner!: HTMLElement;

  @state()
  collapsed = true;

  @state()
  initialized = false;

  @state()
  needsButton = true;

  truncateHeight = 0;

  #resizeObserver?: ResizeObserver;

  protected override onDestroy = () => {
    if (this.button) {
      this.button.removeEventListener('keydown', this.#keydownHandler);
    }

    window.removeEventListener('resize', this.#onResize);
    this.#resizeObserver?.disconnect();
  };

  override render() {
    return html`
      <div
        class="truncate truncate--line-clamped"
        style="--lines: ${this.lines}"
      >
        <div class="truncate__inner">
          <slot @slotchange="${this.#onSlotChange}"></slot>
        </div>
      </div>
      ${this.#buttonTemplate()}
    `;
  }

  #onSlotChange() {
    requestAnimationFrame(() => {
      this.#initializeComponent();
    });
  }

  #getTruncateHeightInPixels() {
    const truncateHeightString = getComputedStyle(
      this.truncate,
    ).getPropertyValue('--truncate-height');

    if (truncateHeightString.endsWith('px')) {
      return parseInt(truncateHeightString, 10);
    }

    if (truncateHeightString.endsWith('rem')) {
      const remValue = parseFloat(truncateHeightString);
      const rootFontSize = parseFloat(
        getComputedStyle(document.documentElement).fontSize,
      );
      return Math.round(remValue * rootFontSize);
    }

    return 0;
  }

  #initializeComponent() {
    if (this.initialized) {
      return;
    }

    this.truncateHeight = this.#getTruncateHeightInPixels();

    if (!this.truncateHeight) {
      const truncateRect = this.truncate.getBoundingClientRect();

      this.truncate.style.setProperty(
        '--truncate-height',
        `${truncateRect.height}px`,
      );
    }

    this.#calculateTruncateApplicable();
    window.addEventListener('resize', this.#onResize);

    this.initialized = true;

    if (this.observeResize) {
      this.#resizeObserver = new ResizeObserver(() => {
        this.recalculateRequiredSpaceContent();
      });
      this.#resizeObserver.observe(this.truncateInner);
    }

    this.button.addEventListener('keydown', this.#keydownHandler);
  }

  #onResize = () => {
    this.#calculateTruncateApplicable();
  };

  recalculateRequiredSpaceContent() {
    if (!this.initialized) return;
    this.#enableTruncate();
    this.#calculateTruncateApplicable();
  }

  #calculateTruncateApplicable() {
    if (!this.truncateHeight) {
      this.#enableTruncate();
      return;
    }

    this.#disableTruncate();

    if (this.truncateInner.scrollHeight > this.truncateHeight) {
      this.#enableTruncate();
    }
  }

  #disableTruncate() {
    this.needsButton = false;
    this.truncate.classList.add('truncate-not-applicable');
  }

  #enableTruncate() {
    this.needsButton = true;
    this.truncate.classList.remove('truncate-not-applicable');
  }

  #buttonTemplate() {
    return html`<footer
      class="footer"
      aria-hidden="true"
      @mouseenter="${this.#onFooterMouseEnter}"
      @mouseleave="${this.#onFooterMouseLeave}"
      part="footer"
      ?hidden=${!this.needsButton}
    >
      <a role="none" tabindex="0" @click="${this.#toggle}"
        >${this.#linkTemplate()}</a
      >
    </footer>`;
  }

  #linkTemplate() {
    if (this.collapsed) {
      return html`<slot name="show-more">${translate('common.show_more')}</slot>
        <a-icon name="caret-down"></a-icon>`;
    }
    return html`<slot name="show-less">${translate('common.show_less')}</slot>
      <a-icon name="caret-up"></a-icon>`;
  }

  #onFooterMouseEnter() {
    this.truncate.classList.add('truncate--footer-hovered');
  }

  #onFooterMouseLeave() {
    this.truncate.classList.remove('truncate--footer-hovered');
  }

  #keydownHandler = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      (event.target as HTMLElement).click();
    }
  };

  #toggle() {
    this.collapsed = !this.collapsed;
    if (this.observeResize) {
      this.#resizeObserver?.disconnect();
    }

    if (this.collapsed) {
      this.#close();
    } else {
      this.#open();
    }

    if (this.observeResize) {
      this.#resizeObserver?.observe(this.truncateInner);
    }
  }

  #open() {
    this.truncate.classList.remove('truncate--line-clamped');

    const truncateInnerRect = this.truncateInner?.getBoundingClientRect();
    this.truncate.style.setProperty(
      '--truncate-height-expanded',
      `${truncateInnerRect?.height}px`,
    );
    this.truncate.classList.add('truncate--expanded');
  }

  #close() {
    const computedStyle = getComputedStyle(this.truncate);
    const duration = computedStyle.getPropertyValue(
      '--tm-transition-duration-normal',
    );

    this.truncate.classList.remove('truncate--expanded');
    setTimeout(
      () => {
        this.truncate.classList.add('truncate--line-clamped');
      },
      parseFloat(duration) * 1000,
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'a-text-collapse': TextCollapseComponent;
  }
}
